if (!customElements.get('media-gallery')) {
  customElements.define(
    'media-gallery',
    class MediaGallery extends HTMLElement {
      constructor() {
        super();
        this.elements = {
          liveRegion: this.querySelector('[id^="GalleryStatus"]'),
          viewer: this.querySelector('[id^="GalleryViewer"]'),
          thumbnails: this.querySelector('[id^="GalleryThumbnails"]'),
        };
        this.mql = window.matchMedia('(min-width: 750px)');
        this.variantChangeUnsubscriber = undefined;
        this.initializeVariantMedia();
        if (!this.elements.thumbnails) return;

        this.elements.viewer.addEventListener('slideChanged', debounce(this.onSlideChanged.bind(this), 500));
        this.elements.thumbnails.querySelectorAll('[data-target]').forEach((mediaToSwitch) => {
          mediaToSwitch
            .querySelector('button')
            .addEventListener('click', this.setActiveMedia.bind(this, mediaToSwitch.dataset.target, false));
        });
        if (this.dataset.desktopLayout.includes('thumbnail') && this.mql.matches) this.removeListSemantic();
      }

      disconnectedCallback() {
        this.variantChangeUnsubscriber?.();
      }

      initializeVariantMedia() {
        const productInfo = this.closest('product-info');
        const selectedVariantJson = productInfo?.querySelector('variant-selects [data-selected-variant]')?.textContent;

        if (typeof subscribe === 'function' && typeof PUB_SUB_EVENTS !== 'undefined') {
          this.variantChangeUnsubscriber = subscribe(PUB_SUB_EVENTS.variantChange, ({ data }) => {
            if (data?.sectionId !== productInfo?.dataset.section) return;
            this.filterVariantMedia(data.variant);
          });
        }

        if (!selectedVariantJson) return;

        try {
          this.filterVariantMedia(JSON.parse(selectedVariantJson));
        } catch (error) {
          console.error(error);
        }
      }

      filterVariantMedia(variant) {
        if (!variant) return;

        const mediaItems = Array.from(this.elements.viewer.querySelectorAll('li[data-media-id]'));
        const thumbnailItems = this.elements.thumbnails
          ? Array.from(this.elements.thumbnails.querySelectorAll('li[data-target]'))
          : [];
        const variantTokens = this.getVariantTokens(variant);

        if (!mediaItems.length || !variantTokens.length) {
          this.resetVariantMediaFilter(mediaItems, thumbnailItems);
          return;
        }

        const matchedMediaIds = new Set(
          mediaItems
            .filter((item) => this.mediaMatchesVariant(item.dataset.variantMediaAlt, variantTokens))
            .map((item) => item.dataset.mediaId)
        );
        const matchingMediaIds = new Set(matchedMediaIds);
        const featuredMediaId = variant.featured_media?.id ? `${this.closest('product-info')?.dataset.section}-${variant.featured_media.id}` : null;

        if (!matchedMediaIds.size) {
          this.resetVariantMediaFilter(mediaItems, thumbnailItems);
          if (featuredMediaId) this.setActiveMedia(featuredMediaId, true);
          return;
        }

        if (featuredMediaId) matchingMediaIds.add(featuredMediaId);

        mediaItems.forEach((item) => {
          item.hidden = !matchingMediaIds.has(item.dataset.mediaId) && !this.isUniversalVariantMedia(item.dataset.variantMediaAlt);
        });
        thumbnailItems.forEach((item) => {
          item.hidden = !matchingMediaIds.has(item.dataset.target) && !this.isUniversalVariantMedia(item.dataset.variantMediaAlt);
        });

        this.refreshVariantMediaSliders();

        const visibleMedia = mediaItems.find((item) => !item.hidden && item.dataset.mediaId === featuredMediaId) || mediaItems.find((item) => !item.hidden);
        if (visibleMedia) this.setActiveMedia(visibleMedia.dataset.mediaId, true);
      }

      resetVariantMediaFilter(mediaItems, thumbnailItems) {
        mediaItems.forEach((item) => {
          item.hidden = false;
        });
        thumbnailItems.forEach((item) => {
          item.hidden = false;
        });
        this.refreshVariantMediaSliders();
      }

      refreshVariantMediaSliders() {
        [this.elements.viewer, this.elements.thumbnails].forEach((sliderComponent) => {
          if (!sliderComponent?.resetPages) return;

          sliderComponent.resetPages();

          const visibleSlides = Array.from(sliderComponent.querySelectorAll('[id^="Slide-"]')).filter(
            (slide) => !slide.hidden
          );
          if (sliderComponent.currentPageElement) sliderComponent.currentPageElement.textContent = visibleSlides.length ? 1 : 0;
          if (sliderComponent.pageTotalElement) sliderComponent.pageTotalElement.textContent = visibleSlides.length || 0;

          const shouldDisableButtons = visibleSlides.length < 2;
          sliderComponent.prevButton?.toggleAttribute('disabled', shouldDisableButtons);
          sliderComponent.nextButton?.toggleAttribute('disabled', shouldDisableButtons);
        });
      }

      getVariantTokens(variant) {
        const values = [variant.title, variant.option1, variant.option2, variant.option3, ...(variant.options || [])];
        return [...new Set(values.map((value) => this.normalizeVariantText(value)).filter(Boolean))].filter(
          (value) => value !== 'default title'
        );
      }

      mediaMatchesVariant(altText, variantTokens) {
        const explicitTags = this.getVariantMediaTags(altText);
        return explicitTags.some((tag) => variantTokens.includes(tag));
      }

      getVariantMediaTags(altText) {
        const text = altText || '';
        const tags = [];
        const bracketMatches = text.matchAll(/\[(?:variant|variants)\s*:\s*([^\]]+)\]/gi);
        const inlineMatches = text.matchAll(/(?:^|[;\n])\s*(?:variant|variants)\s*:\s*([^;\n]+)/gi);

        [...bracketMatches, ...inlineMatches].forEach((match) => {
          match[1].split(/[|,]/).forEach((tag) => tags.push(this.normalizeVariantText(tag)));
        });

        return [...new Set(tags.filter(Boolean))];
      }

      isUniversalVariantMedia(altText) {
        const universalTags = ['all', 'global', 'common'];
        return (
          /\[(all|global|common)\]/i.test(altText || '') ||
          this.getVariantMediaTags(altText).some((tag) => universalTags.includes(tag))
        );
      }

      normalizeVariantText(value) {
        return (value || '')
          .toString()
          .toLowerCase()
          .replace(/[\u2018\u2019]/g, "'")
          .replace(/[\u201c\u201d]/g, '"')
          .replace(/\s+/g, ' ')
          .trim();
      }

      onSlideChanged(event) {
        const thumbnail = this.elements.thumbnails.querySelector(
          `[data-target="${event.detail.currentElement.dataset.mediaId}"]`
        );
        this.setActiveThumbnail(thumbnail);
      }

      setActiveMedia(mediaId, prepend) {
        const activeMedia =
          this.elements.viewer.querySelector(`[data-media-id="${mediaId}"]`) ||
          this.elements.viewer.querySelector('[data-media-id]');
        if (!activeMedia) {
          return;
        }
        this.elements.viewer.querySelectorAll('[data-media-id]').forEach((element) => {
          element.classList.remove('is-active');
        });
        activeMedia?.classList?.add('is-active');

        if (prepend) {
          activeMedia.parentElement.firstChild !== activeMedia && activeMedia.parentElement.prepend(activeMedia);

          if (this.elements.thumbnails) {
            const activeThumbnail = this.elements.thumbnails.querySelector(`[data-target="${mediaId}"]`);
            activeThumbnail?.parentElement.firstChild !== activeThumbnail &&
              activeThumbnail?.parentElement.prepend(activeThumbnail);
          }

          if (this.elements.viewer.slider) this.elements.viewer.resetPages();
        }

        this.preventStickyHeader();
        window.setTimeout(() => {
          if (!this.mql.matches || this.elements.thumbnails) {
            activeMedia.parentElement.scrollTo({ left: activeMedia.offsetLeft });
          }
          const activeMediaRect = activeMedia.getBoundingClientRect();
          // Don't scroll if the image is already in view
          if (activeMediaRect.top > -0.5) return;
          const top = activeMediaRect.top + window.scrollY;
          window.scrollTo({ top: top, behavior: 'smooth' });
        });
        this.playActiveMedia(activeMedia);

        if (!this.elements.thumbnails) return;
        const activeThumbnail = this.elements.thumbnails.querySelector(`[data-target="${mediaId}"]`);
        if (!activeThumbnail) return;
        this.setActiveThumbnail(activeThumbnail);
        this.announceLiveRegion(activeMedia, activeThumbnail.dataset.mediaPosition);
      }

      setActiveThumbnail(thumbnail) {
        if (!this.elements.thumbnails || !thumbnail) return;

        this.elements.thumbnails
          .querySelectorAll('button')
          .forEach((element) => element.removeAttribute('aria-current'));
        thumbnail.querySelector('button').setAttribute('aria-current', true);
        if (this.elements.thumbnails.isSlideVisible(thumbnail, 10)) return;

        this.elements.thumbnails.slider.scrollTo({ left: thumbnail.offsetLeft });
      }

      announceLiveRegion(activeItem, position) {
        const image = activeItem.querySelector('.product__modal-opener--image img');
        if (!image) return;
        image.onload = () => {
          this.elements.liveRegion.setAttribute('aria-hidden', false);
          this.elements.liveRegion.innerHTML = window.accessibilityStrings.imageAvailable.replace('[index]', position);
          setTimeout(() => {
            this.elements.liveRegion.setAttribute('aria-hidden', true);
          }, 2000);
        };
        image.src = image.src;
      }

      playActiveMedia(activeItem) {
        window.pauseAllMedia();
        const deferredMedia = activeItem.querySelector('.deferred-media');
        if (deferredMedia) deferredMedia.loadContent(false);
      }

      preventStickyHeader() {
        this.stickyHeader = this.stickyHeader || document.querySelector('sticky-header');
        if (!this.stickyHeader) return;
        this.stickyHeader.dispatchEvent(new Event('preventHeaderReveal'));
      }

      removeListSemantic() {
        if (!this.elements.viewer.slider) return;
        this.elements.viewer.slider.setAttribute('role', 'presentation');
        this.elements.viewer.sliderItems.forEach((slide) => slide.setAttribute('role', 'presentation'));
      }
    }
  );
}
